// OutlookPanel — Story #6837 Stream 8b (figure 2 = screen-M2-outlook.png).
//
// Left: "Sector size — official statistics". Country chips are the distinct
// countries in the GET's reference[]; the chosen one drives the table, the
// PESTLE and every Regenerate. Rows keep the unit the source published them in,
// with a level pill and a "Refreshed" date.
// Right: "Addressable market — modelled estimate": TAM / SAM / SOM ranges (never a
// single number), the assumptions (editable ones saved through putRangeOverrides),
// dated sources, and the "AI-generated estimate" caption. insufficient_basis shows
// the ConfidenceGate (the range envelope has no status field of its own); a
// completed project with no range shows "Not generated yet" and a Generate button.
//
// Figure 2's "Sector momentum" chart, the "Add country" / ✕ chips and the
// "See every source" link have no data source in the Market GET (the countries
// are fixed by MARKET_COUNTRIES and returned in reference[]), so they are not
// drawn rather than invented.
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Pencil, RefreshCw } from 'lucide-react'
import { putRangeOverrides } from '../../services/api'
import { COUNTRY_LABELS, MARKET_COUNTRIES } from '../../types/market'
import type { MarketPanelProps, MarketRange, MarketRangeAssumption, MarketReferenceRow } from '../../types/market'
import ConfidenceGate from '../shared/ConfidenceGate'
import { compact, currencyPrefix, dateLabel, rangeText, statValue } from './marketFormat'
import { runMarketChain } from './useMarketAutoRun'
import { invalidateMarket } from './marketQueries'

const CARD = 'bg-white/70 border border-[rgba(12,74,110,0.12)] rounded-xl p-4'
const SOURCE_NAME: Record<string, string> = { abs: 'ABS', worldbank: 'World Bank', oecd: 'OECD' }
const LEVEL_STYLE: Record<string, string> = {
  class: 'bg-emerald-50 text-emerald-700',
  division: 'bg-gray-100 text-gray-600',
  section: 'bg-sky-50 text-sky-700',
  anchor: 'bg-sky-100 text-sky-800',
}

const countryName = (c: string) => (COUNTRY_LABELS as Record<string, string>)[c] ?? c

const isShare = (a: MarketRangeAssumption) => a.key.endsWith('_share')

/**
 * How an assumption reads. Shares and the growth rate are fractions on the wire, so they print as percentages;
 * the anchor is the raw number of the official statistic it came from, so it prints in that statistic's own unit
 * (found by matching the reference row it was taken from). null means "no honest way to show it": the row is hidden
 * rather than printing a bare number (an ABS value is in millions, a World Bank value in dollars).
 */
export function assumptionText(a: MarketRangeAssumption, reference: MarketReferenceRow[] = []): string | null {
  if (isShare(a)) return `${Number((a.value * 100).toFixed(1))}%`
  if (a.key === 'growth_rate') return `${Number((a.value * 100).toFixed(1))}% a year`
  if (a.key === 'anchor_value') {
    const row = reference.find((r) => r.value === a.value)
    return row ? `${statValue(row)} (${row.year})` : null
  }
  return compact(a.value)
}

function confidenceBand(c: number): 'high' | 'medium' | 'low' {
  return c >= 0.75 ? 'high' : c >= 0.5 ? 'medium' : 'low'
}
const BAND_STYLE = { high: 'bg-emerald-100 text-emerald-800', medium: 'bg-amber-100 text-amber-800', low: 'bg-red-100 text-red-800' } as const

function httpMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: unknown } }; message?: string } | null | undefined
  const body = e?.response?.data?.error
  return typeof body === 'string' && body ? body : e?.message || 'The change could not be saved.'
}

const ACRONYM: Record<string, string> = {
  TAM: 'Total addressable market: everyone who could buy',
  SAM: 'Serviceable addressable market: the part this invention can reach',
  SOM: 'Serviceable obtainable market: the part it can realistically win',
}

function RangeBar({ label, low, high, top, prefix }: { label: string; low: number; high: number; top: number; prefix: string }) {
  const width = Math.max(3, (high / top) * 100)
  const solid = high > 0 ? (low / high) * 100 : 0
  return (
    <div className="flex items-center gap-3" data-testid={`range-${label.toLowerCase()}`}>
      <abbr title={ACRONYM[label] ?? label} className="w-9 text-xs font-bold text-iflow-900 no-underline">{label}</abbr>
      <div className="h-3.5 flex-1">
        <div
          className="h-full rounded-sm"
          style={{
            width: `${width}%`,
            background: `linear-gradient(90deg, #0EA5E9 0 ${solid}%, #7DD3FC ${solid}% 100%)`,
          }}
        />
      </div>
      <span className="text-xs font-bold text-iflow-900" data-testid={`range-${label.toLowerCase()}-text`}>
        {rangeText(low, high, prefix)}
      </span>
    </div>
  )
}

export default function OutlookPanel({ projectId, market, country, onCountryChange, canRun }: MarketPanelProps) {
  const qc = useQueryClient()
  const [unitMode, setUnitMode] = useState<'local' | 'usd' | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  // ── Left card: official statistics ──────────────────────────────────────
  const present = new Set(market.reference.map((r) => r.country))
  present.add(country)
  const chips = [...MARKET_COUNTRIES.filter((c) => present.has(c)), ...[...present].filter((c) => !(MARKET_COUNTRIES as readonly string[]).includes(c))]
  const forCountry = market.reference.filter((r) => r.country === country)
  const local = forCountry.filter((r) => r.unit !== 'USD')
  const usd = forCountry.filter((r) => r.unit === 'USD')
  const wanted = unitMode ?? (local.length > 0 ? 'local' : 'usd')
  const rows = wanted === 'local' ? (local.length > 0 ? local : usd) : (usd.length > 0 ? usd : local)
  const refreshed = dateLabel(forCountry.map((r) => r.as_of).sort().reverse()[0])

  // ── Right card: modelled range ──────────────────────────────────────────
  const range: MarketRange | null = market.range
  const gated = market.status === 'insufficient_basis'
  const notGenerated = !gated && range === null && market.status === 'completed'
  const prefix = range ? currencyPrefix(range.currency) : ''

  const regenerate = async (force: boolean) => {
    setBusy(true)
    setMessage(null)
    const res = await runMarketChain(projectId, { country, force, classifyFirst: false })
    setBusy(false)
    if (res.phase === 'error') setMessage(res.error)
    else if (res.phase === 'rate_limited') setMessage(`Too many market analyses right now${res.retryAfter != null ? ` — try again in ${res.retryAfter} s` : ''}.`)
    void invalidateMarket(qc, projectId)
  }

  const saveOverride = useMutation({
    mutationFn: (v: { key: string; value: number }) => putRangeOverrides(projectId, { [v.key]: v.value }),
    onSuccess: () => {
      setMessage(null)
      setEditing(null)
      return invalidateMarket(qc, projectId)
    },
    onError: (err) => setMessage(httpMessage(err)),
  })

  const startEdit = (a: MarketRangeAssumption) => {
    setEditing(a.key)
    setDraft(String(isShare(a) ? Number((a.value * 100).toFixed(2)) : a.value))
    setMessage(null)
  }
  const commitEdit = (a: MarketRangeAssumption) => {
    const n = Number(draft)
    const value = isShare(a) ? n / 100 : n
    if (draft.trim() === '' || !Number.isFinite(value) || value < 0 || (isShare(a) && value > 1) || value > 1e13) {
      setMessage(isShare(a) ? 'Enter a share between 0 and 100.' : 'Enter a number between 0 and 10 trillion.')
      return
    }
    saveOverride.mutate({ key: a.key, value })
  }

  const differs = range !== null && range.country !== country

  return (
    <div data-testid="outlook-panel" className="grid gap-3 lg:grid-cols-5">
      <div className={`${CARD} lg:col-span-3`} data-testid="outlook-statistics">
        <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-bold text-iflow-900">Sector size — official statistics</h3>
            <p className="text-[11px] text-gray-500">Primary sector · value added, latest year published · <strong>fact layer</strong></p>
          </div>
          {refreshed && (
            <span data-testid="outlook-refreshed" className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-sky-700">
              Refreshed {refreshed}
            </span>
          )}
        </div>

        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Country">
            {chips.map((c) => (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={c === country}
                data-testid={`country-chip-${c}`}
                onClick={() => onCountryChange(c)}
                className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                  c === country ? 'border-sky-400 bg-sky-50 text-iflow-700' : 'border-[rgba(12,74,110,0.2)] bg-white text-gray-600 hover:text-gray-800'
                }`}
              >
                {countryName(c)}
              </button>
            ))}
          </div>
          <div className="flex gap-0.5 rounded-lg bg-sky-500/10 p-0.5" role="tablist" aria-label="Currency">
            {([['local', 'Local currency'], ['usd', 'USD']] as const).map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={wanted === k} data-testid={`unit-${k}`} onClick={() => setUnitMode(k)}
                className={`rounded-md px-2 py-0.5 text-[10.5px] font-semibold ${wanted === k ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {rows.length === 0 ? (
          <p data-testid="outlook-no-statistics" className="py-6 text-center text-xs text-gray-500">
            {market.reference.length === 0
              ? 'No official statistics yet — they appear once a sector is assigned.'
              : `No official statistics are published for ${countryName(country)} at this sector yet.`}
          </p>
        ) : (
          <table className="w-full text-left text-xs" data-testid="outlook-table">
            <thead>
              <tr className="border-b border-[rgba(12,74,110,0.15)] text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <th className="py-1.5 pr-2">Country</th>
                <th className="py-1.5 pr-2">Sector</th>
                <th className="py-1.5 pr-2">Value added</th>
                <th className="py-1.5 pr-2">Year</th>
                <th className="py-1.5 pr-2">Source</th>
                <th className="py-1.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.country}-${r.source}-${r.unit}`} data-testid={`stat-row-${r.source}`} className="border-b border-[rgba(12,74,110,0.08)]">
                  <td className="py-2 pr-2 font-bold text-iflow-900">{countryName(r.country)}</td>
                  <td className="py-2 pr-2 text-gray-700">ISIC {r.section}</td>
                  <td className="py-2 pr-2 font-bold text-iflow-900">{statValue(r)}</td>
                  <td className="py-2 pr-2 text-gray-700">{r.year}</td>
                  <td className="py-2 pr-2 text-gray-700">
                    {r.source_url && r.source_url.startsWith('https://')
                      ? <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="text-sky-700 underline">{SOURCE_NAME[r.source] ?? r.source}</a>
                      : (SOURCE_NAME[r.source] ?? r.source)}
                  </td>
                  <td className="py-2">
                    <span data-testid="stat-level" className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-medium ${LEVEL_STYLE[r.level] ?? 'bg-gray-100 text-gray-600'}`}>
                      {r.level === 'anchor' ? 'anchor' : `${r.level} level`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="mt-3 text-[11px] text-gray-500">
          Shown at the finest level the official source publishes for this sector — a class where one exists, otherwise the
          division. Figures are pulled from the publishing agency and never estimated.
        </p>
      </div>

      <div className="space-y-3 lg:col-span-2">
        {message && (
          <div data-testid="outlook-message" role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{message}</div>
        )}

        {gated && (
          <div className="rounded-xl border border-dashed border-amber-400 bg-amber-50/60 p-2" data-testid="outlook-gate">
            <ConfidenceGate
              compact
              icon={<AlertTriangle />}
              title="Not enough signal to estimate an addressable market"
              description="The documents on this invention do not support a trustworthy sector, so no market range was estimated — the platform never fabricates a figure. Add a description or more documents and the estimate runs automatically."
            />
          </div>
        )}

        {notGenerated && (
          <div className={CARD} data-testid="outlook-not-generated">
            <h3 className="text-sm font-bold text-iflow-900">Addressable market — modelled estimate</h3>
            <p className="my-3 text-xs text-gray-600">Not generated yet.</p>
            {canRun && (
              <button type="button" data-testid="outlook-generate" disabled={busy} onClick={() => regenerate(false)}
                className="flex items-center gap-1 rounded-lg bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} /> Generate
              </button>
            )}
          </div>
        )}

        {range && (
          <div className={CARD} data-testid="outlook-range">
            <div className="mb-2 flex items-start justify-between gap-2">
              <div>
                <h3 className="text-sm font-bold text-iflow-900">Addressable market — modelled estimate</h3>
                <p className="text-[11px] text-gray-500"><strong>Estimate layer</strong> · built from the sector anchor and the sources below</p>
              </div>
              <span data-testid="range-confidence" className={`shrink-0 rounded-lg px-2 py-1 text-[11px] font-semibold ${BAND_STYLE[confidenceBand(range.confidence)]}`}>
                Confidence {confidenceBand(range.confidence)}
              </span>
            </div>

            <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]">
              <span data-testid="range-country-badge" className="rounded-full bg-sky-50 px-2 py-0.5 font-medium text-sky-700">
                generated for {countryName(range.country)}
              </span>
              {range.stale && (
                <span data-testid="range-stale" className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">out of date — regenerate</span>
              )}
            </div>

            {differs && (
              <div data-testid="range-country-notice" className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Statistics shown for {countryName(country)}; the range was generated for {countryName(range.country)}
                {canRun ? ' — ' : '.'}
                {canRun && (
                  <button type="button" data-testid="range-regenerate-country" disabled={busy} onClick={() => regenerate(true)} className="font-semibold underline disabled:opacity-50">
                    Regenerate for {countryName(country)}
                  </button>
                )}
              </div>
            )}

            <div className="space-y-2">
              <RangeBar label="TAM" low={range.tam[0]} high={range.tam[1]} top={range.tam[1]} prefix={prefix} />
              <RangeBar label="SAM" low={range.sam[0]} high={range.sam[1]} top={range.tam[1]} prefix={prefix} />
              <RangeBar label="SOM" low={range.som[0]} high={range.som[1]} top={range.tam[1]} prefix={prefix} />
            </div>

            <h4 className="mb-1 mt-3 text-xs font-bold text-iflow-900">Assumptions — edit any and the range recomputes</h4>
            <ul className="divide-y divide-[rgba(12,74,110,0.08)]" data-testid="range-assumptions">
              {range.assumptions.filter((a) => assumptionText(a, market.reference) !== null).map((a) => (
                <li key={a.key} data-testid={`assumption-${a.key}`} className="flex items-center justify-between gap-2 py-1.5 text-xs">
                  <span className="text-gray-700">{a.label}</span>
                  {editing === a.key ? (
                    <span className="flex shrink-0 items-center gap-1">
                      <input
                        aria-label={`Edit ${a.label}`}
                        data-testid={`assumption-input-${a.key}`}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        className="w-16 rounded border border-sky-400 px-1 py-0.5 text-right text-xs"
                      />
                      {isShare(a) && <span className="text-gray-500">%</span>}
                      <button type="button" data-testid={`assumption-save-${a.key}`} disabled={saveOverride.isPending} onClick={() => commitEdit(a)} className="rounded bg-sky-500 px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-50">Save</button>
                      <button type="button" onClick={() => setEditing(null)} className="text-[11px] text-gray-500 underline">Cancel</button>
                    </span>
                  ) : (
                    <span className="flex shrink-0 items-center gap-1.5">
                      <span className="font-semibold text-iflow-900">{assumptionText(a, market.reference)}</span>
                      {range.overrides && a.key in range.overrides && <span className="rounded bg-violet-100 px-1 text-[10px] font-bold uppercase text-violet-800">edited</span>}
                      {a.editable ? (
                        canRun && (
                          <button type="button" aria-label={`Edit ${a.label}`} data-testid={`assumption-edit-${a.key}`} onClick={() => startEdit(a)} className="rounded border border-[rgba(12,74,110,0.2)] bg-white p-1 text-gray-600">
                            <Pencil className="h-3 w-3" />
                          </button>
                        )
                      ) : (
                        <span className="text-[10.5px] text-gray-500">locked</span>
                      )}
                    </span>
                  )}
                </li>
              ))}
            </ul>

            <h4 className="mb-1 mt-3 text-xs font-bold text-iflow-900">
              Sources used ({range.sources.length}) <span className="font-normal text-gray-500">· built from the reference statistics</span>
            </h4>
            <ol className="space-y-0.5 text-[11.5px]" data-testid="range-sources">
              {range.sources.map((s, i) => (
                <li key={s.url} className="flex gap-1.5">
                  <span className="rounded bg-sky-50 px-1 text-[10.5px] font-bold text-sky-700">{i + 1}</span>
                  {s.url.startsWith('https://')
                    ? <a href={s.url} target="_blank" rel="noopener noreferrer" className="min-w-0 break-words text-sky-700 underline">{s.title || s.url}</a>
                    : <span>{s.title || 'Source'}</span>}
                </li>
              ))}
            </ol>

            <p className="mt-3 text-[11px] text-gray-500" data-testid="range-caption">
              AI-generated estimate as at {dateLabel(range.generated_at) ?? 'an unknown date'} · not investment advice · ranges, never a
              single number · recomputed when the sector statistics or PESTLE refresh.
            </p>

            {canRun && (
              <button type="button" data-testid="range-regenerate" disabled={busy} onClick={() => regenerate(true)}
                className="mt-2 flex items-center gap-1 rounded-lg border border-[rgba(12,74,110,0.2)] bg-white px-2.5 py-1 text-xs font-medium text-iflow-700 disabled:opacity-50">
                <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} /> Regenerate for {countryName(country)}
              </button>
            )}
          </div>
        )}

        {!gated && !notGenerated && range === null && (
          <div className={CARD} data-testid="outlook-pending">
            <h3 className="text-sm font-bold text-iflow-900">Addressable market — modelled estimate</h3>
            <p className="my-3 text-xs text-gray-600">The estimate appears once this invention has been categorised.</p>
          </div>
        )}
      </div>
    </div>
  )
}
